import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface LocalStorageContextType {
  getItem: (key: string | undefined | null) => string | null;
  setItem: (
    key: string | undefined | null,
    value: string,
    ttl?: number
  ) => void;
  removeItem: (key: string | undefined | null) => void;
}

const LocalStorageContext = createContext<LocalStorageContextType | undefined>(
  undefined
);

/**
 * This provider maintains a local cache of `localStorage` values to optimize access and updates.
 * It exposes methods to get, set, and remove items from `localStorage` via context, allowing
 * components to interact with browser storage in a React-friendly way.
 *
 * Usage:
 * - Wrap your component tree with `LocalStorageProvider` to provide access to the context.
 * - Use the `useLocalStorage` hook to access `getItem`, `setItem`, and `removeItem` methods.
 *
 * @example
 * ```tsx
 * <LocalStorageProvider>
 *   <App />
 * </LocalStorageProvider>
 * ```
 */
export const LocalStorageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Initialize cache with current localStorage values
  const [cache, setCache] = useState<Record<string, string | null>>({
    ...localStorage,
  });

  // https://developer.mozilla.org/en-US/docs/Web/API/StorageEvent
  // Listen for storage events to keep cache in sync across tabs/windows.
  // Note: Storage events do not fire in the same tab that made the change.
  const handleStorageChange = useCallback((_event: StorageEvent) => {
    setCache({ ...localStorage });
  }, []);

  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  /**
   * Helper functions for accessing localStorage with cache synchronization.
   *
   * Each writes to the cache, so a caller that both writes and depends on the function it wrote with
   * would re-run for as long as the identity kept changing. `setItem` and `removeItem` are stable
   * for that reason. `getItem` has to change with the cache so readers see a write, which also gives
   * the context object a new identity on every write: a caller should depend on the individual
   * helper it uses rather than on the context object.
   */
  const setItem = useCallback(
    (
      key: string | undefined | null,
      value: string,
      ttl = 86_400_000 // Default TTL: 1 day in milliseconds
    ) => {
      if (!key) return null;

      const now = new Date();
      const item = {
        value,
        expiry: now.getTime() + ttl,
      };

      localStorage.setItem(key, JSON.stringify(item));
      setCache((prevCache) => ({ ...prevCache, [key]: JSON.stringify(item) }));
    },
    []
  );

  const removeItem = useCallback((key: string | undefined | null) => {
    if (!key) return;

    localStorage.removeItem(key);
    setCache((prevCache) => {
      const newCache = { ...prevCache };
      delete newCache[key];
      return newCache;
    });
  }, []);

  const getItem = useCallback(
    (key: string | undefined | null) => {
      if (!key) return null;
      const value = cache[key] || localStorage.getItem(key);

      if (typeof value === 'string') {
        try {
          const item = JSON.parse(value);
          const now = new Date();

          // Check for expiry
          if (item.expiry && now.getTime() > item.expiry) {
            // Item has expired
            removeItem(key);
            return null;
          }

          return item.value;
        } catch {
          return value;
        }
      }

      return null;
    },
    [cache, removeItem]
  );

  const storage = useMemo(
    () => ({ getItem, setItem, removeItem }),
    [getItem, setItem, removeItem]
  );

  return (
    <LocalStorageContext.Provider value={storage}>
      {children}
    </LocalStorageContext.Provider>
  );
};

/**
 * Custom hook to access the local storage context.
 *
 * A hook or effect that needs one of these methods as a dependency should list that method, not the
 * object it came from: `getItem` tracks the cache, so the object itself is new after every write.
 *
 * @returns The local storage context methods.
 * @example
 * ```tsx
 * const { getItem, setItem, removeItem } = useLocalStorage();
 * const value = getItem('myKey');
 * ```
 */
export const useLocalStorage = (): LocalStorageContextType => {
  const context = useContext(LocalStorageContext);
  if (!context) {
    throw new Error(
      'useLocalStorage must be used within a LocalStorageProvider'
    );
  }
  return context;
};

export const makeBatchUploadLocalStorageKey = ({
  profileId,
  organizationsId,
}: {
  profileId?: string;
  organizationsId?: string;
}): string | null => {
  if (!profileId || !organizationsId) return null;
  return `batch-upload-${profileId}-${organizationsId}`;
};
